import { Skeleton } from "@credit-acceptance/uikit";
import { CancelledError, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import PageFooter from "../../components/layout/PageFooter";
import TopBar from "../../components/layout/TopBar";
import { useAuth } from "../../hooks/useAuth";
import { computeShouldForceExit, useForceExitCheck } from "../../hooks/useForceExitCheck";
import { redirectToOriginSite } from "../../lib/exitRedirect";
import { getErrorMessage } from "../../lib/query/errors";
import { useReadOnly as useReadOnlyStore } from "../../stores/approvalStore";
import { useForceExitStore } from "../../stores/forceExitStore";
import ActionFabCluster from "../actions/ActionFabCluster";
import { useDocumentBroadcaster } from "../documents/coord/useDocumentBroadcaster";
import { useDocumentMoveListener } from "../documents/coord/useDocumentMoveListener";
import DocumentsAndMessagesPanel from "../documents/DocumentsAndMessagesPanel";
import { useDocumentViewerStore } from "../documents/documentViewerStore";
import { useViewerSessionState } from "../documents/sessionState";
import ExitAccountModal from "../exit/ExitAccountModal";
import ForceExitWarningModal from "../exit/ForceExitWarningModal";
import { releaseHeldVlaLock } from "../lock/api/releaseHeldVlaLock";
import { useVlaLock } from "../lock/api/useVlaLock";
import DealLockBanner from "../lock/components/DealLockBanner";
import { ReadOnlyProvider } from "../lock/ReadOnlyContext";
import StipulationsPanel from "../stipulations/StipulationsPanel";
import { applicationStatusQuery, useApplicationStatus } from "./api/useApplicationStatus";
import { useDscWorkbench } from "./api/useDscWorkbench";
import DealIntelligenceCard from "./components/DealIntelligenceCard";
import { mapDashboardToDealIntelligenceCard } from "./components/dealIntelligenceCard.mapping";
import FooterAction from "./components/FooterAction";
import Signers, { type SignersHandle } from "./sections/Signers/Signers";

/**
 * Releases the VLA In-Use lock (only if the current user holds it) before
 * leaving the deal, so the next analyst isn't blocked by a stale lock. Awaits
 * the release so the DELETE lands before we navigate away (best-effort — a
 * failed release never blocks the exit), then redirects back to the site
 * that sent the user into the workbench.
 * @returns {Promise<void>} Resolves once the release attempt settles.
 */
async function handleExit(): Promise<void> {
  await releaseHeldVlaLock();
  redirectToOriginSite();
}

/**
 * Releases the held VLA In-Use lock before signing out, for the same reason as
 * {@link handleExit}.
 * @returns {Promise<void>} Resolves once the release attempt settles.
 */
async function handleSignOut(): Promise<void> {
  await releaseHeldVlaLock();
  // TODO: perform the actual sign-out call once wired to AuthProvider — await
  // it before redirecting so navigating away doesn't abort the request.
  redirectToOriginSite();
}

function ReviewPage() {
  const { t } = useTranslation();
  const { approvalId } = useParams<{ approvalId: string }>();
  const { user } = useAuth();

  // Acquire the VLA In-Use lock for this deal on entry; release on exit. When
  // another analyst holds it (or acquisition fails), the deal is read-only and
  // DealLockBanner explains why.
  useVlaLock(approvalId ?? "");
  // Read-only whenever we don't hold the lock (acquiring, held-by-other, or
  // error) — hide Save entirely in every one of those states.
  const readOnly = useReadOnlyStore();

  // The footer Exit button opens the "Exit the Account" modal; the actual
  // release-lock + navigate side-effects run once the analyst confirms there.
  const [exitModalOpen, setExitModalOpen] = useState(false);

  // Force-exit warning modal — shown when pre-approval is voided and
  // user attempts to save. Blocks the save and prompts the analyst to exit.
  const [forceExitModalOpen, setForceExitModalOpen] = useState(false);

  // Drive the dual-window document viewer (OR-22558): broadcast the active
  // document + session signal to the viewer window. Mounted here (not in the
  // panel) so it answers the viewer's handshake for the whole deal session.
  useDocumentBroadcaster(useViewerSessionState());

  // React to the viewer moving a document to a different approval (OR-23048):
  // drop the stale focused-document selection and refetch the docs list.
  useDocumentMoveListener(approvalId ?? "");

  // Drop any focused-document selection when the open deal changes (or on exit)
  // so a viewer opened later doesn't get replayed the previous deal's document
  // via the handshake.
  const clearFocusedDocument = useDocumentViewerStore((state) => state.clearFocusedDocument);
  useEffect(() => {
    clearFocusedDocument();
  }, [approvalId, clearFocusedDocument]);

  // Fetch the DSC Workbench dashboard for this deal. Both the Deal Intelligence
  // Card and the Signers card derive their data from this response.
  const { data, isLoading, isError, error } = useDscWorkbench(approvalId ?? "");

  // Application status + phase is a separate, non-blocking read. The card
  // renders as soon as the dashboard resolves; status/phase populate once this
  // call returns, and a failure leaves the status blank (no card-level error).
  const { data: applicationStatus } = useApplicationStatus(approvalId ?? "");

  // Check if the force-exit condition is met (pre-approval voided when not in read-only
  // mode). This triggers immediately on app load and blocks saves if the condition is true.
  const { shouldForceExit } = useForceExitCheck({
    isPreApprovalVoid: applicationStatus?.isPreApprovalVoid,
    readOnly,
  });

  // Update the force-exit store so all components can check before mutations.
  const setShouldForceExit = useForceExitStore((state) => state.setShouldForceExit);
  useEffect(() => {
    setShouldForceExit(shouldForceExit);
  }, [shouldForceExit, setShouldForceExit]);

  // If force-exit condition is met on load, show the warning immediately.
  useEffect(() => {
    if (shouldForceExit) {
      setForceExitModalOpen(true);
    }
  }, [shouldForceExit]);

  const queryClient = useQueryClient();
  // True while handleSave's click-time status re-check is in flight — disables
  // Save so a second click can't race the fetch.
  const [isCheckingForceExit, setIsCheckingForceExit] = useState(false);
  // Set when the click-time status re-check itself fails (network error) —
  // we block the save rather than risk saving over a voided pre-approval.
  const [forceExitCheckError, setForceExitCheckError] = useState<string>();

  // ReviewPage isn't remounted on approvalId route-param changes, so a
  // handleSave call started for one deal must not act on the result once the
  // analyst has navigated to another deal while the fetch was in flight.
  const approvalIdReference = useRef(approvalId);
  useEffect(() => {
    approvalIdReference.current = approvalId;
  }, [approvalId]);

  // Imperative handle on the Signers card — lets handleSave trigger its
  // submit without reaching into its DOM by id.
  const signersReference = useRef<SignersHandle>(null);

  // Handle save: re-fetch application status fresh (bypassing the query's
  // staleTime) at the moment Save is clicked, so the force-exit decision uses
  // the latest isPreApprovalVoid rather than whatever was cached on load. If
  // voided, show the warning modal and abort; otherwise submit the Signers
  // form (the only save-able section today). Shared by the footer Save button
  // and the Exit modal's Save & Exit — resolves `true` only once the signers
  // form actually saves, so callers can gate exit-navigation on it.
  // TODO: wire to AuthProvider once it exists (see folder-structure.md §providers)
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (isCheckingForceExit) {
      return false;
    }

    if (!approvalId) {
      setForceExitCheckError(t("forceExit.missingApprovalId"));
      return false;
    }

    const requestedApprovalId = approvalId;
    setForceExitCheckError(undefined);
    setIsCheckingForceExit(true);
    try {
      const status = await queryClient.fetchQuery({
        ...applicationStatusQuery(requestedApprovalId),
        staleTime: 0,
      });

      // Deal changed while the fetch was in flight — this result no longer
      // applies; don't pop the modal or submit against the wrong deal.
      if (approvalIdReference.current !== requestedApprovalId) {
        return false;
      }

      const isForceExit = computeShouldForceExit({ isPreApprovalVoid: status.isPreApprovalVoid, readOnly });
      // Sync the shared store immediately so useSaveSignerApplicant's
      // mutationFn (which reads the store's live value, not a stale render)
      // agrees with this fresh check before requestSubmit runs synchronously.
      setShouldForceExit(isForceExit);

      if (isForceExit) {
        setForceExitModalOpen(true);
        return false;
      }

      return (await signersReference.current?.requestSubmit()) ?? false;
    } catch (statusCheckError) {
      // A cancelled/superseded query (e.g. dedup'd against another refetch)
      // isn't a real status-check failure — don't block Save or show an error.
      if (approvalIdReference.current === requestedApprovalId && !(statusCheckError instanceof CancelledError)) {
        setForceExitCheckError(getErrorMessage(statusCheckError));
      }
      return false;
    } finally {
      setIsCheckingForceExit(false);
    }
  }, [approvalId, isCheckingForceExit, queryClient, readOnly, setShouldForceExit, t]);

  const dealCardViewModel = data
    ? mapDashboardToDealIntelligenceCard(data, approvalId ?? "", applicationStatus)
    : undefined;

  return (
    <ReadOnlyProvider value={readOnly}>
      <div className="bg-background text-foreground flex h-screen flex-col overflow-hidden">
        <TopBar userName={user?.name} userRole={user?.userRole} onSignOut={() => void handleSignOut()} />
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-2 overflow-y-auto p-2 lg:grid-cols-12 lg:overflow-hidden">
          <main className="flex min-h-0 flex-col lg:col-span-9 lg:overflow-hidden">
            {/* VLA In-Use lock banner — shown only when another analyst holds the
                lock or acquisition failed; otherwise renders nothing. */}
            <DealLockBanner />
            {/* Deal Intelligence Card — loading skeleton, error, or live data. */}
            {isLoading && (
              <div
                role="status"
                aria-label={t("dealIntelligenceCard.loading")}
                className="bg-primary sticky top-0 z-10 rounded px-3 py-2 lg:px-4 lg:py-3"
              >
                <Skeleton loading active paragraph={{ rows: 2, widths: [220, 320] }} />
              </div>
            )}
            {isError && (
              <p role="alert" className="text-destructive px-3 py-2 text-xs">
                {t("dealIntelligenceCard.loadError", { message: getErrorMessage(error) })}
              </p>
            )}
            {dealCardViewModel && <DealIntelligenceCard data={dealCardViewModel} />}

            <div className="flex w-full flex-col gap-2 py-2 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
              {/* key on approvalId so switching deals remounts the form and re-seeds
                  its defaultValues from the new response (RHF reads them once at mount). */}
              {data && (
                <Signers
                  ref={signersReference}
                  key={approvalId}
                  driver={data.driver}
                  secondSigner={data.secondSigner}
                  approvalId={approvalId}
                />
              )}
            </div>
            {forceExitCheckError && (
              <p role="alert" className="text-destructive px-3 py-2 text-xs">
                {t("forceExit.checkError", { message: forceExitCheckError })}
              </p>
            )}
            <FooterAction
              onSave={() => void handleSave()}
              onExit={() => setExitModalOpen(true)}
              hideSave={readOnly}
              isCheckingForceExit={isCheckingForceExit}
            />
          </main>
          {/* pb reserves clearance for the fixed FAB cluster (bottom-9/h-12,
              md:bottom-11/h-12) so the aside's height stops above it instead of
              the scrollable panel rendering underneath. */}
          <aside className="flex min-h-0 flex-col gap-2 pb-12 md:pb-14 lg:col-span-3 lg:h-full">
            <DocumentsAndMessagesPanel approvalId={approvalId ?? ""} dealerId={data?.dealSummary?.dealerId} />
            <StipulationsPanel approvalId={approvalId ?? ""} />
          </aside>
        </div>
        <PageFooter />
        <ActionFabCluster approvalId={approvalId ?? ""} />
        <ExitAccountModal
          open={exitModalOpen}
          onOpenChange={setExitModalOpen}
          approvalId={approvalId ?? ""}
          currentStatus={applicationStatus?.statusDescription}
          currentPhase={applicationStatus?.phaseDescription}
          roleName={user?.userRole}
          onSaveApplicant={handleSave}
          onExit={() => void handleExit()}
        />
        <ForceExitWarningModal open={forceExitModalOpen} onOpenChange={setForceExitModalOpen} onConfirm={handleExit} />
      </div>
    </ReadOnlyProvider>
  );
}

export default ReviewPage;
